/**
 * RBAC claim helpers, read from `@cyngn/auth-react` (RBAC Phase 7, DEV-3779).
 *
 * The canonical role names, the two claim names and the decoding of an ID token now come from
 * the shared package instead of from a copy kept here, so the SPA cannot drift from what
 * `cyngn-auth-py` verifies on the server. This module stays as the app's entry point: it keeps
 * the names and shapes the components already import, and it keeps the one behaviour that is
 * this app's own — `rolesFromClaims` returns literal group membership, and no ladder is applied
 * anywhere in the UI (see `AuthProvider.hasRole`).
 *
 * Everything here still decodes the ID token *without* verifying its signature, so the results
 * are only good enough to decide what UI to show. The server is the authority on every access
 * decision (see DEV-4482 / DEV-3744).
 */
import {
  CANONICAL_ROLES as LIBRARY_CANONICAL_ROLES,
  claimCompanyIds,
  claimRoles,
  decodeClaims,
  effectiveRole,
  isCanonicalRole,
  userFromIdToken,
  type AuthConfig,
  type AuthUser,
  type CanonicalRole as LibraryCanonicalRole,
  type TokenClaims,
} from "@cyngn/auth-react";

export type CanonicalRole = LibraryCanonicalRole;

export const CANONICAL_ROLES: readonly CanonicalRole[] =
  LIBRARY_CANONICAL_ROLES;

export const GROUPS_CLAIM = "cognito:groups";
export const COMPANY_IDS_CLAIM = "custom:company_ids";

export interface IdTokenClaims extends TokenClaims {
  sub?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  exp?: number;
  token_use?: string;
  [GROUPS_CLAIM]?: string[];
  [COMPANY_IDS_CLAIM]?: string;
}

/**
 * The claim names the library's readers use.
 *
 * `readAuthConfig` is deliberately not called: it throws when the pool or the client id is
 * absent, which is right for starting a login flow but wrong for reading a token the app
 * already holds. Only the two claim names matter here, and a deployment that renames them sets
 * the same `AUTH_OIDC_*` variables the backend reads.
 */
const claimConfig = (): AuthConfig => {
  const env = window._env_ || ({} as typeof window._env_);
  return {
    groupsClaim: env.AUTH_OIDC_GROUPS_CLAIM || GROUPS_CLAIM,
    companiesClaim: env.AUTH_OIDC_COMPANIES_CLAIM || COMPANY_IDS_CLAIM,
  } as AuthConfig;
};

/**
 * Decode an ID token's claim set, or null when the token is missing/unreadable.
 */
export const parseIdTokenClaims = (
  rawIdToken: string | null | undefined
): IdTokenClaims | null => decodeClaims(rawIdToken) as IdTokenClaims | null;

/**
 * Canonical roles carried by the groups claim. Groups outside the canonical set are dropped so
 * a stray group can never grant UI a role never planned for.
 */
export const rolesFromClaims = (
  claims: IdTokenClaims | null
): CanonicalRole[] => (claims ? claimRoles(claims, claimConfig()) : []);

/**
 * The role to display: the most privileged human role held, or `Cyngn-Vehicle` when that is the
 * only one. No hierarchy is applied to access decisions — `hasRole` stays literal group
 * membership.
 */
export const primaryRoleFromClaims = (
  claims: IdTokenClaims | null
): CanonicalRole | null => effectiveRole(rolesFromClaims(claims));

/**
 * Company ids from the tenancy claim: a comma-separated list or a list attribute, de-duplicated.
 */
export const companyIdsFromClaims = (claims: IdTokenClaims | null): string[] =>
  claims ? claimCompanyIds(claims, claimConfig()) : [];

export const toCanonicalRole = (value: string): CanonicalRole | null =>
  isCanonicalRole(value) ? value : null;

/**
 * The session an ID token describes, in the shared package's shape: `sub`, roles, company ids
 * and the issue/expiry times the observability fields are computed from. Null when the token is
 * unreadable or carries no `sub`.
 */
export const authUserFromIdToken = (
  rawIdToken: string | null | undefined
): AuthUser | null => userFromIdToken(rawIdToken, claimConfig());
